import { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { HeroSlider } from './HeroSlider';
import { fetchPlacementBanners, trackBannerEvent } from '../../lib/bannerApi';

/**
 * The homepage hero.
 *
 * Banners published to `hero_main` in Banner Management replace the built-in
 * category slider entirely. That is deliberate rather than a merge: the
 * built-in slides are a split layout (copy left, product art right) while an
 * uploaded banner is a finished 1920×700 composition with its own typography.
 * Alternating between the two reads as two different websites.
 *
 * With nothing published, the built-in slider runs exactly as before — so the
 * homepage never depends on the banner service being up or populated.
 */

const AUTO_MS = 6000;

export function HomeHero() {
  const [banners, setBanners] = useState(null);   // null = still deciding
  const [index, setIndex] = useState(0);
  const [ratio, setRatio] = useState(null);
  const [paused, setPaused] = useState(false);
  const reduced = useReducedMotion();
  const seen = useRef(new Set());
  const ref = useRef(null);

  useEffect(() => {
    let cancelled = false;
    fetchPlacementBanners('hero_main')
      .then((list) => { if (!cancelled) setBanners(list); })
      .catch(() => { if (!cancelled) setBanners([]); });
    return () => { cancelled = true; };
  }, []);

  const count = banners?.length || 0;
  const go = useCallback((n) => setIndex((n + count) % (count || 1)), [count]);

  // Auto-advance only when there is more than one, and never while hovered or
  // when the visitor has asked for reduced motion.
  useEffect(() => {
    if (count < 2 || paused || reduced) return;
    const t = setInterval(() => setIndex((i) => (i + 1) % count), AUTO_MS);
    return () => clearInterval(t);
  }, [count, paused, reduced]);

  const active = count ? banners[index] : null;

  // One impression per banner per page view, counted when it is on screen.
  useEffect(() => {
    const node = ref.current;
    if (!active || !node) return;
    const io = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && !seen.current.has(active.banner_id)) {
        seen.current.add(active.banner_id);
        trackBannerEvent(active.banner_id, 'view');
      }
    }, { threshold: 0.4 });
    io.observe(node);
    return () => io.disconnect();
  }, [active]);

  // Still deciding: hold the space rather than flashing the built-in slider and
  // then replacing it a moment later.
  if (banners === null) {
    return <div className="w-full animate-pulse bg-slate-100" style={{ aspectRatio: '1920 / 700', maxHeight: '70vh' }} />;
  }

  if (!count) return <HeroSlider />;

  const onLoad = (e) => {
    const { naturalWidth: w, naturalHeight: h } = e.currentTarget;
    if (w && h) setRatio(w / h);
  };

  const slide = (b) => (
    <picture>
      <source media="(max-width: 640px)" srcSet={b.mobile_image_url || b.desktop_image_url} />
      <img
        src={b.desktop_image_url}
        alt={b.title || ''}
        onLoad={onLoad}
        /* `contain`, matching the category hero: uploads are validated to ±8%
           of the slot ratio, so a slightly-off file letterboxes by a few pixels
           instead of having its edges cut away. */
        className="h-full w-full object-contain"
        loading="eager"
        decoding="async"
      />
    </picture>
  );

  const body = (
    <div
      ref={ref}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      className="relative w-full overflow-hidden"
      style={{
        aspectRatio: ratio ? String(ratio) : '1920 / 700',
        maxHeight: '70vh',
        background: active.background_color || '#F1F5F9',
      }}
    >
      <AnimatePresence mode="wait">
        <motion.div
          key={active.banner_id}
          className="absolute inset-0"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: reduced ? 0.15 : 0.5 }}
        >
          {active.cta_url
            ? <Link to={active.cta_url} onClick={() => trackBannerEvent(active.banner_id, 'click')} className="block h-full w-full">
                {slide(active)}
              </Link>
            : slide(active)}
        </motion.div>
      </AnimatePresence>

      {Number(active.overlay) > 0 && (
        <div className="pointer-events-none absolute inset-0 bg-slate-900" style={{ opacity: Number(active.overlay) / 100 }} />
      )}

      {/* Copy overlays only when the admin supplied some — most hero art already
          carries its own headline. */}
      {(active.title || active.subtitle || active.cta_label) && (
        <div className={`pointer-events-none absolute inset-0 flex flex-col justify-center gap-2 px-6 md:px-14 ${
          active.text_align === 'center' ? 'items-center text-center'
            : active.text_align === 'right' ? 'items-end text-right' : 'items-start'}`}>
          {active.title && (
            <h1 className="font-heading text-2xl leading-tight text-secondary drop-shadow-sm md:text-5xl">
              {active.title}
            </h1>
          )}
          {active.subtitle && (
            <p className="max-w-xl font-body text-sm text-secondary/80 md:text-base">{active.subtitle}</p>
          )}
          {active.cta_label && (
            <span className="pointer-events-auto mt-1 inline-flex rounded-full bg-primary px-6 py-2.5 font-body font-semibold text-white shadow-lg">
              {active.cta_label}
            </span>
          )}
        </div>
      )}

      {count > 1 && (
        <>
          <button
            onClick={() => go(index - 1)} aria-label="Previous banner"
            className="absolute left-3 top-1/2 grid h-9 w-9 -translate-y-1/2 place-items-center rounded-full bg-white/80 text-secondary shadow-md backdrop-blur transition-colors hover:bg-white md:left-5"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <button
            onClick={() => go(index + 1)} aria-label="Next banner"
            className="absolute right-3 top-1/2 grid h-9 w-9 -translate-y-1/2 place-items-center rounded-full bg-white/80 text-secondary shadow-md backdrop-blur transition-colors hover:bg-white md:right-5"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
          <div className="absolute bottom-3 left-1/2 flex -translate-x-1/2 gap-1.5 md:bottom-5">
            {banners.map((b, i) => (
              <button
                key={b.banner_id} onClick={() => go(i)}
                aria-label={`Go to banner ${i + 1}`}
                aria-current={i === index}
                className={`h-1.5 rounded-full transition-all ${
                  i === index ? 'w-7 bg-primary' : 'w-4 bg-white/70 hover:bg-white'}`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );

  return body;
}

export default HomeHero;
